import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import set from 'lodash.set';
import mergeWith from 'lodash.mergewith';
import { navigate } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import { useQuery } from '@apollo/client';
import qs from 'query-string';
import mapApolloData from 'gatsby-theme-core/src/helpers/map-apollo-data';
import useVehiclesToShow from './hooks/use-vehicles-to-show';
import GET_LIVE_VEHICLES from './query';

export const Context = createContext(null);

const vehiclesKey = `vehiclesVehicles${process.env.GATSBY_STRAPI_VERSION === '5' ? '_connection' : ''}`;

const keys = {
  priceMin: {
    key: 'lowestPrice',
    operator: 'gte',
    type: 'int',
  },
  priceMax: {
    key: 'lowestPrice',
    operator: 'lt',
    type: 'int',
  },
  buildyear: {
    key: 'buildyear',
    operator: 'eq',
    type: 'int',
  },
  buildyearMin: {
    key: 'buildyear',
    operator: 'gte',
    type: 'int',
  },
  buildyearMax: {
    key: 'buildyear',
    operator: 'lt',
    type: 'int',
  },
  odometerMin: {
    key: 'odometer',
    operator: 'gte',
    type: 'int',
  },
  odometerMax: {
    key: 'odometer',
    operator: 'lt',
    type: 'int',
  },
  maxTowing: {
    key: 'maxTowing',
    operator: 'eq',
    type: 'int',
  },
  maxTowingMin: {
    key: 'maxTowing',
    operator: 'gte',
    type: 'int',
  },
  maxTowingMax: {
    key: 'maxTowing',
    operator: 'lt',
    type: 'int',
  },
  rangeMin: {
    key: 'range',
    operator: 'gte',
    type: 'int',
  },
  rangeMax: {
    key: 'range',
    operator: 'lt',
    type: 'int',
  },
  electricRange: {
    key: 'range',
    operator: 'in',
    type: 'int',
  },
  electricRangeMin: {
    key: 'electricRange',
    operator: 'gte',
    type: 'int',
  },
  electricRangeMax: {
    key: 'electricRange',
    operator: 'lt',
    type: 'int',
  },
  powerHp: {
    key: 'powerHp',
    operator: 'in',
    type: 'int',
  },
  powerHpMin: {
    key: 'powerHp',
    operator: 'gte',
    type: 'int',
  },
  powerHpMax: {
    key: 'powerHp',
    operator: 'lt',
    type: 'int',
  },
  leaseMin: {
    key: 'leases.maandbedrag',
    operator: 'gte',
    type: 'int',
  },
  leaseMax: {
    key: 'leases.maandbedrag',
    operator: 'lt',
    type: 'int',
  },
  externalLeaseMin: {
    key: 'externalLeases.price',
    operator: 'gte',
    type: 'int',
  },
  externalLeaseMax: {
    key: 'externalLeases.price',
    operator: 'lt',
    type: 'int',
  },
  privateLeaseMin: {
    key: 'privateLease.price',
    operator: 'gte',
    type: 'int',
  },
  privateLeaseMax: {
    key: 'privateLease.price',
    operator: 'lt',
    type: 'int',
  },
  company: {
    key: 'company.name',
    operator: 'in',
    type: 'string',
  },
  seats: {
    key: 'seats',
    operator: 'in',
    type: 'int',
  },
  doors: {
    key: 'doors',
    operator: 'in',
    type: 'int',
  },
  taxAddition: {
    key: 'taxAddition',
    operator: 'in',
    type: 'int',
  },
  range: {
    key: 'range',
    operator: 'in',
    type: 'int',
  },
  dateOfArrivalMin: {
    key: 'dateOfArrival',
    operator: 'gte',
    type: 'string',
  },
};
const keyAllowlist = [
  ...Object.keys(keys),
  'size',
  'make',
  'model',
  'body',
  'fuel',
  'transmission',
  'baseColor',
  'energylabel',
  'taxType',
  'isSold',
  'isNewVehicle',
  'vehicleType',
  'pluginHybrid',
];
const allowedSearchParams = (params) => Object
  .fromEntries(Object.entries(params).filter(([key]) => keyAllowlist.includes(key)));

const convertFilterValue = (type, value, isArr) => {
  if (isArr) {
    return value.map((val) => convertFilterValue(type, val));
  }
  switch (value) {
    case 'false':
    case false:
      return false;
    case 'true':
      return true;
    case 'onbekend':
      return 'onbekend'; // allow onbekend for nulling
    default:
      break;
  }
  switch (type) {
    case 'int':
      return value ? Number(value) : undefined;
    default:
      return value ?? undefined;
  }
};

const getOperator = (key, isArr) => {
  if (key.endsWith('_null')) {
    return 'null';
  }
  if (key.endsWith('_notNull')) {
    return 'notNull';
  }
  const isNegative = key.endsWith('_not');
  const arrOperator = isNegative ? 'notIn' : 'in';
  const singleOperator = isNegative ? 'ne' : 'eq';
  return isArr ? arrOperator : singleOperator;
};

const setFilter = (key, operator, convertedVal, isArr) => {
  if ((!isArr && convertedVal === 'onbekend')) {
    return set({}, key, {
      null: true,
    });
  }
  if (isArr && convertedVal.includes('onbekend')) {
    const withoutOnbekend = convertedVal.filter((v) => v !== 'onbekend');

    const nullified = set({}, key, {
      null: true,
    });

    const orIn = (withoutOnbekend.length > 0 ? set({}, key, {
      [operator]: [...withoutOnbekend],
    }) : null);

    return {
      and: [{
        or: [
          nullified,
          orIn,
        ].filter(Boolean),
      }],
    };
  }

  return set({}, key, {
    [operator]: convertedVal,
  });
};

// https://github.com/strapi/strapi/releases/tag/v4.13.1
// as of Strapi 4.13.1 we cannot query incorrect param anymore
// make sure to not empty a undefined value
const convertFilter = (key, value) => {
  const isArr = Array.isArray(value);

  const predefinedKey = keys[key];
  if (predefinedKey) {
    const convertedVal = convertFilterValue(predefinedKey.type, value, isArr);
    if (convertedVal === null || convertedVal === undefined) {
      return {};
    }
    return setFilter(predefinedKey.key, predefinedKey.operator, convertedVal, isArr);
  }

  const operator = getOperator(key, isArr);
  const convertedVal = convertFilterValue(operator, value, isArr);
  if (convertedVal === null || convertedVal === undefined) {
    return {};
  }

  // remove all _not and _notNull operators
  const ky = key.split('_')[0];
  return setFilter(ky, operator, convertedVal, isArr);
};

// eslint-disable-next-line consistent-return
function customizer(objValue, srcValue) {
  if (Array.isArray(objValue)) {
    return objValue.concat(srcValue);
  }
}

const Provider = ({
  groupedOptions = [],
  staticCount,
  staticVehicles = [],
  limit = 10,
  defaultSorts = ['name:asc'],
  defaultWhere = {},
  replaceUrl = true,
  possibleLayouts = ['column', 'row'],
  layoutKey = 'search',
  srp: defaultSrp,
  children,
  demandLogin = false, // gatsby-theme-user functionality
  skip = false, // set by gatsby-theme-user, when user is not logged in (yet)
}) => {
  // we allow resetting the SRP:
  // the SRP field changes SRP; changes srpSlug, changes vdp slugPrefix
  // redirect to different URL when used on a stockPage
  const [srp, setSrp] = useState(defaultSrp);
  const srpSlug = srp.pages[0].slug;
  const location = useLocation();
  const {
    sort: sortParam,
    ...searchParams
  } = qs.parse(location.search);
  // concat to make sure we can use single or array fields
  const [sorts, setSorts] = useState(sortParam ? [].concat(sortParam) : []);
  const searchFields = allowedSearchParams(searchParams);
  const [tempWhere, setWhere] = useState({
    ...searchFields,
  });
  const { predefined = {}, ...restWhere } = tempWhere;
  const where = useMemo(() => ({
    ...predefined, // if SRP filter is selected
    ...restWhere,
  }), [predefined, restWhere]);

  // remove all entries in the where that are already in the defaultValues, keep the qs clean
  const uniqueWhere = Object.entries(where).reduce((acc, [key, value]) => {
    // convert to string to be able to compare arrays
    if (Object.hasOwn(defaultWhere, key) && String(defaultWhere[key]) === String(value)) {
      return acc;
    }
    return {
      ...acc,
      [key]: value,
    };
  }, {});

  const queryString = qs.stringify({
    sort: sorts,
    ...uniqueWhere,
  });

  useEffect(() => {
    if (!replaceUrl) {
      return;
    }

    const newPathname = `${srpSlug}/${queryString ? `?${queryString}` : ''}`;
    navigate(newPathname, { replace: true });
  }, [
    replaceUrl,
    srpSlug,
    queryString,
  ]);

  const resetFilters = useCallback(() => {
    setWhere({});
  }, [setWhere]);

  useEffect(() => {
    if (!replaceUrl) {
      return;
    }
    // if we are replacing the url,
    // the search is empty
    // but we still have filters?
    // probably clicked a link to the same page
    if (!location.search) {
      setSorts([]);
      resetFilters();
    }
  }, [
    resetFilters,
    replaceUrl,
    defaultSorts,
    location.search,
  ]);

  // Fetch live data
  const {
    loading,
    fetchMore,
    error,
    data,
  } = useQuery(GET_LIVE_VEHICLES, {
    variables: {
      limit,
      start: 0,
      sort: sorts.length > 0 ? sorts : defaultSorts,
      filters: Object.entries({
        ...defaultWhere,
        ...where,
      }).reduce((acc, [key, value]) => mergeWith(
        acc,
        convertFilter(key, value),
        customizer,
      ), {}),
    },
    skip,
    context: {
      useAuth: !!demandLogin, // does nothing when gatsby-theme-user is not installed
    },
    notifyOnNetworkStatusChange: true,
  });

  const policyFailed = error?.graphQLErrors.some(({ message }) => message === 'Policy Failed');

  if (error && !policyFailed) {
    console.error(error);
    if (typeof Sentry !== 'undefined') {
      Sentry.captureException(error, (scope) => {
        scope.clear();
        scope.setContext('defaultWhere', defaultWhere);
        scope.setContext('where', where);
        return scope;
      });
    }
  }

  const mappedVehicles = (data?.[vehiclesKey]?.data || [])
    .map((v) => mapApolloData(v));
  const currentLoadedVehicles = mappedVehicles.length ?? 0;

  // call this function to load more results for this query
  const loadMoreVehicles = useCallback(() => fetchMore({
    variables: {
      start: currentLoadedVehicles,
    },
    updateQuery: (prev, { fetchMoreResult }) => {
      if (!fetchMoreResult) {
        return prev;
      }
      return {
        ...prev,
        [vehiclesKey]: {
          ...prev[vehiclesKey], // meta
          data: [
            ...prev[vehiclesKey].data,
            ...fetchMoreResult[vehiclesKey].data,
          ],
        },
      };
    },
  }), [
    fetchMore,
    currentLoadedVehicles,
  ]);

  const { vehiclesToShow, showLoading, totalNumberOfVehicles } = useVehiclesToShow({
    staticVehicles,
    staticCount,
    queryString,
    vehicles: mappedVehicles,
    count: (data?.[vehiclesKey]?.pageInfo || data?.[vehiclesKey]?.meta?.pagination || {}).total,
    loading,
  });

  if (!loading && totalNumberOfVehicles === 0) {
    console.info('Zero vehicles have been found');
    // Sentry.withScope(function(scope) {
    //   Object.keys(filterState).forEach(key => {
    //     scope.setExtra(key, filterState[key]);
    //   });
    //   scope.setLevel('info');
    //   Sentry.captureException(new Error('Got 0 vehicles from server'));
    // });
  }

  // feature layout:
  // this only works in the frontend
  // maybe one day we can read a cookie in SSR?
  const [layout, setLayout] = useState(possibleLayouts[0]);
  const key = `@vehicles/layout/${layoutKey}`;

  useEffect(() => {
    const storedLayout = window.sessionStorage.getItem(key);
    if (storedLayout && possibleLayouts.includes(storedLayout)) {
      setLayout(storedLayout);
    }
  }, [
    key,
    possibleLayouts,
  ]);

  useEffect(() => {
    window.sessionStorage.setItem(key, layout);
  }, [
    key,
    layout,
  ]);

  const searching = useMemo(() => Object.entries(uniqueWhere).some(([whereKey, whereValue]) => {
    if (!whereKey) {
      return false;
    }
    if (whereValue === false) {
      return true;
    }
    if (!whereValue) {
      return false;
    }
    return true;
  }), [uniqueWhere]);

  const value = useMemo(() => ({
    data,
    vehicles: vehiclesToShow,
    loading,
    showLoading,
    keys,
    // make sure { model: undefined } will not be counted
    activeFiltersCount: Object.values(uniqueWhere).filter(Boolean).length,
    where: tempWhere,
    defaultWhere,
    setWhere,
    to: `${srpSlug}${queryString ? `?${queryString}` : ''}`,
    searching,

    groupedOptions,

    srp,
    // the srp could have been resetted by the SRP field. Use the given default
    setSrp: (x) => setSrp(x || defaultSrp),

    sorts,
    setSorts,

    resetFilters,
    loadMoreVehicles,
    totalNumberOfVehicles,
    moreVehiclesAvailable: (totalNumberOfVehicles ?? 0) - currentLoadedVehicles,

    layout,
    setLayout,
    possibleLayouts,

    demandLogin,
    policyFailed,
  }), [
    data,
    vehiclesToShow,
    loading,
    showLoading,
    uniqueWhere,
    tempWhere,
    defaultWhere,
    setWhere,
    searching,
    queryString,
    groupedOptions,
    srp,
    srpSlug,
    setSrp,
    defaultSrp,
    sorts,
    setSorts,
    resetFilters,
    loadMoreVehicles,
    totalNumberOfVehicles,
    currentLoadedVehicles,
    layout,
    setLayout,
    possibleLayouts,
    demandLogin,
    policyFailed,
  ]);

  return (
    <Context.Provider value={value}>
      {children}
    </Context.Provider>
  );
};

export default Provider;
